import { useCallback, useMemo, useState } from "react";

import {
  SearchAutocomplete,
  type SearchItem,
} from "@vega-core/components/Form/Controls/Input/SearchAutocomplete/SearchAutocomplete";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";

import { type Article } from "../types/article";
import { type FAQSection } from "../types/faq";

type Props = {
  articles: Article[];
  faqSections: FAQSection[];
  labels: SearchLabels;
  onArticleSelect: (slug: string) => void;
  onFaqSelect: (sectionIndex: number, targetId: string) => void;
};

enum SearchResultKind {
  Article = "article",
  Faq = "faq",
}

enum FaqMatchKind {
  Answer = "faq-answer",
  Question = "faq-question",
  Section = "faq-section",
}

type SearchLabels = {
  articlesGroupLabel: string;
  faqGroupLabel: string;
  faqSectionLabel: string;
};

type SearchResult = SearchItem & {
  kind: SearchResultKind;
  label: string;
  slug?: string;
  sectionIndex?: number;
  targetId?: string;
  description?: string;
  showGroupLabel?: boolean;
  groupLabel?: string;
};

export const ResourcesSearch = ({
  articles,
  faqSections,
  labels,
  onArticleSelect,
  onFaqSelect,
}: Props) => {
  const groupLabels = {
    articles: labels.articlesGroupLabel,
    faq: labels.faqGroupLabel,
  } as const;
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 200);

  const normalizedQuery = debouncedQuery.trim().toLowerCase();
  const hasQuery = normalizedQuery.length > 0;
  const matchesQuery = useCallback(
    (value: string) => value.toLowerCase().includes(normalizedQuery),
    [normalizedQuery],
  );

  const articleResults = useMemo(() => {
    if (!hasQuery) {
      return [];
    }

    return articles
      .filter(
        (article) =>
          matchesQuery(article.title) || matchesQuery(article.description),
      )
      .map((article) => ({
        id: `article-${article.id}`,
        kind: SearchResultKind.Article,
        label: article.title,
        description: article.description,
        slug: article.slug,
      }));
  }, [articles, hasQuery, matchesQuery]);

  const faqResults = useMemo(() => {
    if (!hasQuery) {
      return [];
    }

    return faqSections.flatMap((section, sectionIndex) => {
      const results: {
        id: string;
        kind: FaqMatchKind;
        label: string;
        description?: string;
        sectionIndex: number;
        targetId: string;
      }[] = [];

      if (matchesQuery(section.title)) {
        results.push({
          id: `faq-section-${section.value}`,
          kind: FaqMatchKind.Section,
          label: section.title,
          description: labels.faqSectionLabel,
          sectionIndex,
          targetId: `faq-section-${section.value}`,
        });
      }

      section.items.forEach((item, itemIndex) => {
        const bodyText = typeof item.body === "string" ? item.body : "";
        const questionMatch = matchesQuery(item.title);
        const answerMatch = bodyText ? matchesQuery(bodyText) : false;

        if (questionMatch || answerMatch) {
          results.push({
            id: `faq-item-${section.value}-${itemIndex}`,
            kind: answerMatch ? FaqMatchKind.Answer : FaqMatchKind.Question,
            label: item.title,
            description:
              answerMatch && !questionMatch
                ? `${section.title} · Answer match`
                : section.title,
            sectionIndex,
            targetId: `faq-${section.value}-${itemIndex}`,
          });
        }
      });

      return results;
    });
  }, [faqSections, hasQuery, matchesQuery]);

  const searchItems = useMemo(() => {
    if (!hasQuery) {
      return [] as SearchResult[];
    }

    const articleItems: SearchResult[] = articleResults.map((result) => ({
      id: result.id,
      kind: SearchResultKind.Article,
      label: result.label,
      description: result.description,
      slug: result.slug,
      searchValue: `${result.label} ${result.description}`,
      content: null,
      groupLabel: groupLabels.articles,
    }));

    const faqItems: SearchResult[] = faqResults.map((result) => ({
      id: result.id,
      kind: SearchResultKind.Faq,
      label: result.label,
      description: result.description,
      sectionIndex: result.sectionIndex,
      targetId: result.targetId,
      searchValue: `${result.label} ${result.description ?? ""}`,
      content: null,
      groupLabel: groupLabels.faq,
    }));

    return [...articleItems, ...faqItems].map((item, index, items) => ({
      ...item,
      showGroupLabel: index === 0 || items[index - 1].kind !== item.kind,
    }));
  }, [articleResults, faqResults, hasQuery]);

  const handleItemSelect = useCallback(
    (item: SearchResult | null) => {
      if (!item) {
        return;
      }

      if (item.kind === SearchResultKind.Article && item.slug) {
        onArticleSelect(item.slug);

        return;
      }

      if (
        item.kind === SearchResultKind.Faq &&
        item.sectionIndex !== undefined &&
        item.targetId
      ) {
        onFaqSelect(item.sectionIndex, item.targetId);
      }
    },
    [onArticleSelect, onFaqSelect],
  );

  return (
    <SearchAutocomplete
      clearOnSelect
      items={searchItems}
      name="resources-search"
      onChange={(value) => setQuery(value)}
      onItemSelect={handleItemSelect}
      placeholder={m.localcms_resources_search_placeholder()}
      renderItem={(item) => (
        <div className="flex flex-col gap-1 w-full">
          {item.showGroupLabel ? (
            <span className="body-small-medium text-text/secondary uppercase tracking-wide">
              {item.groupLabel}
            </span>
          ) : null}
          <span className="body-base-medium text-text/primary">
            {item.label}
          </span>
          {item.description ? (
            <span className="body-small-regular text-text/secondary line-clamp-2">
              {item.description}
            </span>
          ) : null}
        </div>
      )}
    />
  );
};
